import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import DrawIcon from '@mui/icons-material/Draw';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { apiFetch, openAuthenticatedUpload } from '../../api/client';
import { HUD_DIALOG_CONTENT, HUD_DIALOG_PAPER, HUD_DIALOG_TITLE, HUD_CTA_BUTTON } from './hudDialogStyles';

const novaClausula = () => ({
  id: `c-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  titulo: '',
  texto: '',
});

/**
 * Editor de minuta (nº contrato, preâmbulos, cláusulas).
 * Persistência só via API /minuta — sem localStorage de negócio.
 */
export default function EditorMinutaClausulas({
  open,
  onClose,
  processoId,
  readOnly = false,
  onSaved = null,
  onGerarAssinatura = null,
}) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [erro, setErro] = useState('');
  const [msg, setMsg] = useState('');
  const [numeroContrato, setNumeroContrato] = useState('');
  const [preambuloIsg, setPreambuloIsg] = useState('');
  const [preambuloPrestador, setPreambuloPrestador] = useState('');
  const [clausulas, setClausulas] = useState([novaClausula()]);
  const [codigoProcesso, setCodigoProcesso] = useState('');
  const [pdfPath, setPdfPath] = useState(null);
  const [gerandoPdf, setGerandoPdf] = useState(false);

  const carregar = useCallback(async () => {
    if (!processoId || !open) return;
    setLoading(true);
    setErro('');
    try {
      const res = await apiFetch(`/api/contratos/processos/${processoId}/minuta`);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Erro ao carregar minuta');
      setCodigoProcesso(data.codigo || '');
      setNumeroContrato(data.numeroContrato || data.codigo || '');
      setPreambuloIsg(data.preambuloIsg || '');
      setPreambuloPrestador(data.preambuloPrestador || '');
      setPdfPath(data.pdfPath || null);
      const lista = Array.isArray(data.clausulas) ? data.clausulas : [];
      setClausulas(
        lista.length
          ? lista.map((c, i) => ({
              id: c.id || `c-${i}`,
              titulo: c.titulo || c.nome || '',
              texto: c.texto || c.conteudo || '',
              fixo: !!c.fixo,
            }))
          : [novaClausula()]
      );
    } catch (e) {
      setErro(e.message || 'Falha ao carregar');
    } finally {
      setLoading(false);
    }
  }, [processoId, open]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const atualizarClausula = (idx, field, value) => {
    setClausulas((prev) => prev.map((c, i) => (i === idx ? { ...c, [field]: value } : c)));
  };

  const adicionar = () => setClausulas((prev) => [...prev, novaClausula()]);
  const remover = (idx) => {
    setClausulas((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((_, i) => i !== idx);
    });
  };

  const salvar = async () => {
    if (!processoId) return;
    setSaving(true);
    setErro('');
    setMsg('');
    try {
      const res = await apiFetch(`/api/contratos/processos/${processoId}/minuta`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          numeroContrato: numeroContrato || null,
          preambuloIsg: preambuloIsg || null,
          preambuloPrestador: preambuloPrestador || null,
          titulo: `Minuta ${numeroContrato || codigoProcesso || processoId}`,
          clausulas: clausulas.map((c) => ({
            id: c.id,
            titulo: c.titulo,
            texto: c.texto,
            fixo: !!c.fixo,
          })),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Erro ao salvar minuta');
      if (Array.isArray(data.clausulas) && data.clausulas.length) {
        setClausulas(
          data.clausulas.map((c, i) => ({
            id: c.id || `c-${i}`,
            titulo: c.titulo || '',
            texto: c.texto || '',
            fixo: !!c.fixo,
          }))
        );
      }
      setMsg('Minuta salva no servidor.');
      onSaved?.(data);
    } catch (e) {
      setErro(e.message || 'Falha ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const gerarEAssinar = async () => {
    await salvar();
    onGerarAssinatura?.({
      processoId,
      numeroContrato: numeroContrato || codigoProcesso,
      titulo: `Assinatura — Minuta ${numeroContrato || codigoProcesso || processoId}`,
    });
  };

  const gerarPdf = async () => {
    if (!processoId) return;
    setGerandoPdf(true);
    setErro('');
    setMsg('');
    try {
      await salvar();
      const res = await apiFetch(`/api/contratos/processos/${processoId}/minuta/gerar-pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          numeroContrato: numeroContrato || null,
          preambuloIsg: preambuloIsg || null,
          preambuloPrestador: preambuloPrestador || null,
          titulo: `Minuta ${numeroContrato || codigoProcesso || processoId}`,
          clausulas: clausulas.map((c) => ({
            id: c.id,
            titulo: c.titulo,
            texto: c.texto,
            conteudo: c.texto,
            fixo: !!c.fixo,
          })),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Erro ao gerar PDF');
      setPdfPath(data.pdf_path || null);
      setMsg('PDF da minuta gerado e salvo no servidor.');
      onSaved?.(data);
      if (data.pdf_path || data.pdf_url) {
        openAuthenticatedUpload(data.pdf_path || data.pdf_url);
      }
    } catch (e) {
      setErro(e.message || 'Falha ao gerar PDF');
    } finally {
      setGerandoPdf(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: HUD_DIALOG_PAPER }}>
      <DialogTitle sx={HUD_DIALOG_TITLE}>
        <Typography variant="h6" fontWeight={800} sx={{ color: '#fff' }}>
          Minuta — cláusulas contratuais
        </Typography>
        {codigoProcesso && (
          <Typography variant="body2" sx={{ color: '#99f6e4', mt: 0.5 }}>
            Processo {codigoProcesso}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent dividers sx={HUD_DIALOG_CONTENT}>
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <Stack spacing={2} sx={{ pt: 1 }}>
            {erro && (
              <Alert severity="error" onClose={() => setErro('')}>
                {erro}
              </Alert>
            )}
            {msg && (
              <Alert severity="success" onClose={() => setMsg('')}>
                {msg}
              </Alert>
            )}
            {readOnly && (
              <Alert severity="warning">Somente o setor de Contratos (ou admins) pode editar a minuta.</Alert>
            )}

            <TextField
              label="Nº do Contrato"
              size="small"
              fullWidth
              value={numeroContrato}
              onChange={(e) => setNumeroContrato(e.target.value)}
              disabled={readOnly}
              helperText="Gerado/sugerido a partir do código do processo; editável pelo setor de Contratos"
            />
            <TextField
              label="Preâmbulo ISG"
              size="small"
              fullWidth
              multiline
              minRows={3}
              value={preambuloIsg}
              onChange={(e) => setPreambuloIsg(e.target.value)}
              disabled={readOnly}
            />
            <TextField
              label="Preâmbulo Prestador"
              size="small"
              fullWidth
              multiline
              minRows={3}
              value={preambuloPrestador}
              onChange={(e) => setPreambuloPrestador(e.target.value)}
              disabled={readOnly}
            />

            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle2" fontWeight={800}>
                Cláusulas contratuais
              </Typography>
              {!readOnly && (
                <Button startIcon={<AddIcon />} onClick={adicionar} sx={{ textTransform: 'none' }}>
                  Adicionar
                </Button>
              )}
            </Stack>

            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width="28%">Título</TableCell>
                  <TableCell>Cláusula</TableCell>
                  <TableCell width={48} />
                </TableRow>
              </TableHead>
              <TableBody>
                {clausulas.map((c, idx) => (
                  <TableRow key={c.id || idx}>
                    <TableCell>
                      <TextField
                        size="small"
                        fullWidth
                        value={c.titulo}
                        onChange={(e) => atualizarClausula(idx, 'titulo', e.target.value)}
                        disabled={readOnly || c.fixo}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        fullWidth
                        multiline
                        minRows={2}
                        value={c.texto}
                        onChange={(e) => atualizarClausula(idx, 'texto', e.target.value)}
                        disabled={readOnly || c.fixo}
                      />
                    </TableCell>
                    <TableCell>
                      {!readOnly && !c.fixo && (
                        <IconButton size="small" onClick={() => remover(idx)} aria-label="Remover cláusula">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2.5, py: 1.5 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>
          Fechar
        </Button>
        {!readOnly && (
          <>
            <Button
              variant="outlined"
              startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
              disabled={saving || loading || gerandoPdf}
              onClick={salvar}
              sx={{ textTransform: 'none' }}
            >
              Salvar minuta
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              startIcon={gerandoPdf ? <CircularProgress size={16} /> : <PictureAsPdfIcon />}
              disabled={saving || loading || gerandoPdf}
              onClick={gerarPdf}
              sx={{ textTransform: 'none' }}
            >
              {pdfPath ? 'Regenerar PDF' : 'Gerar PDF'}
            </Button>
            <Button
              variant="contained"
              startIcon={<DrawIcon />}
              disabled={saving || loading || gerandoPdf}
              onClick={gerarEAssinar}
              sx={{ ...HUD_CTA_BUTTON }}
            >
              Gerar contrato / Assinar
            </Button>
          </>
        )}
        {readOnly && pdfPath && (
          <Button
            variant="outlined"
            startIcon={<PictureAsPdfIcon />}
            onClick={() => openAuthenticatedUpload(pdfPath)}
            sx={{ textTransform: 'none' }}
          >
            Abrir PDF
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
